<template>
  <IntercomModal
    v-model:show="showModal"
    :title="category ? $t('categories.editCategory') : $t('categories.addCategory')"
    width="600px"
  >
    <div class="category-form">
      <IntercomInput
        v-model="formData.name"
        :label="$t('categories.name')"
        :placeholder="$t('categories.name')"
        required
        :error="errors.name"
      />

      <IntercomInput
        v-model="formData.description"
        type="textarea"
        :label="$t('categories.description')"
        :placeholder="$t('categories.description')"
        :rows="3"
      />
    </div>

    <template #footer>
      <IntercomButton variant="secondary" @click="handleCancel">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton variant="primary" :loading="isLoading" @click="handleSubmit">
        {{ $t('common.save') }}
      </IntercomButton>
    </template>
  </IntercomModal>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { IntercomModal, IntercomInput, IntercomButton } from '@shared/components/intercom'
import { useBilling } from '../composables/useBilling.js'

const props = defineProps({
  show: Boolean,
  category: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['update:show', 'success'])

const { t } = useI18n()
const { createCategory, updateCategory } = useBilling()

// State
const showModal = ref(props.show)
const isLoading = ref(false)

// Form data
const formData = reactive({
  name: '',
  description: '',
})

// Form errors
const errors = reactive({
  name: '',
})

// Watch show prop
watch(
  () => props.show,
  (val) => {
    showModal.value = val
    if (val && props.category) {
      // Edit mode - populate form
      Object.assign(formData, {
        name: props.category.name,
        description: props.category.description || '',
      })
    } else {
      // Create mode - reset form
      resetForm()
    }
    // Clear errors when opening
    clearErrors()
  }
)

// Watch modal state
watch(showModal, (val) => {
  emit('update:show', val)
})

/**
 * Clear errors
 */
const clearErrors = () => {
  errors.name = ''
}

/**
 * Validate form
 */
const validateForm = () => {
  clearErrors()
  let isValid = true

  // Validate name
  if (!formData.name || formData.name.trim() === '') {
    errors.name = 'Category name is required'
    isValid = false
  }

  return isValid
}

/**
 * Reset form
 */
const resetForm = () => {
  Object.assign(formData, {
    name: '',
    description: '',
  })
  clearErrors()
}

/**
 * Handle cancel
 */
const handleCancel = () => {
  showModal.value = false
  resetForm()
}

/**
 * Handle submit
 */
const handleSubmit = async () => {
  // Validate form
  if (!validateForm()) {
    return
  }

  isLoading.value = true

  try {
    // Prepare data
    const categoryData = {
      name: formData.name,
      description: formData.description || null,
    }

    // Create or update
    if (props.category) {
      await updateCategory(props.category.id, categoryData)
      console.log(t('categories.updateSuccess'))
    } else {
      await createCategory(categoryData)
      console.log(t('categories.createSuccess'))
    }

    // Close modal and emit success
    showModal.value = false
    resetForm()
    emit('success')
  } catch (error) {
    console.error('Failed to save category:', error)
    // TODO: Show error notification
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.category-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}
</style>
